import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { cancelBooking, fetchBookingByReference, fetchHotelBySlug, getBookingDetails, sendCancelBookingEmail, updateBookingInDatabase, updateStatus } from "../api-client";
import { BookingResponse } from "../../../backend/src/models/bookingDetails";
import UpdateGuestDetailsForm from "./UpdateGuestDetailsPopup";
import { Booking } from "../pages/MyBookings";
import { useAppContext } from "../contexts/AppContext";
import { useSearchContext } from "../contexts/SearchContext";
import { HotelType } from "../../../backend/src/shared/types";
import Swal from 'sweetalert2';
import { getUsdToGbpExchangeRate } from '../api-client';

const BookingDetails: React.FC = () => {
    const { responseToken } = useParams<{ responseToken: string }>();
    const location = useLocation();
    const holdOrPaid = location.state?.holdOrPaid;
    const [booking, setBooking] = useState<BookingResponse | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [timer, setTimer] = useState<string | null>(null);
    const [bookingFromDB, setBookingFromDB] = useState<Booking | null>(null);
    const { user } = useAppContext();
    const navigate = useNavigate();
    const { adultCount, childCount } = useSearchContext();
    const [hotel, setHotel] = useState<HotelType | null>(null);
    const [rate, setRate] = useState<number | null>(null);

    useEffect(() => {
        fetchDetails();
    }, [responseToken]);

    useEffect(() => {
        if (bookingFromDB && holdOrPaid === 'hold') {
            startCountdown(bookingFromDB!.createdAt);
        }
    }, [bookingFromDB]);

    const getBookingByReference = async (reference: string) => {
        try {
            const data = await fetchBookingByReference(reference);
            setBookingFromDB(data);
        } catch (error) {
            console.error("Error fetching booking details from database:", error);
        }
    };

    function getCurrentDateTime() {
        const date = new Date();

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    const generateSlug = (subject: string): string => {
        return subject
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, "")
            .replace(/\s+/g, "-");
    };

    const getHotel = async (subject: string) => {
        try {
            const slug = generateSlug(subject); // Convert to slug
            const fetchedHotel = await fetchHotelBySlug(slug);
            setHotel(fetchedHotel);
        } catch (error) {
            console.error("Error fetching hotel details:", error);
        }
    };

    useEffect(() => {
        if (booking?.subject) {
            getHotel(booking.subject);
        }
    }, [booking]);

    const fetchDetails = async () => {
        try {
            setIsLoading(true);
            const data = await getBookingDetails(responseToken!);
            setBooking(data);

            if (data.reference) {
                await getBookingByReference(data.reference);
            }
        } catch (error) {
            setError("Failed to fetch booking details. Please try again later.");
            console.error("Error fetching booking details:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const startCountdown = (createdAt: string) => {
        const bookedTime = new Date(createdAt).getTime();
        const countdownTime = bookedTime + 96 * 60 * 60 * 1000; // 96 hours in milliseconds

        const updateTimer = () => {
            const currentTime = new Date().getTime();
            const timeLeft = countdownTime - currentTime;

            if (timeLeft > 0) {
                const hours = Math.floor(timeLeft / (1000 * 60 * 60)); // Total hours remaining
                const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60)); // Remaining minutes after hours
                const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000); // Remaining seconds after minutes
                setTimer(`${hours}h ${minutes}m ${seconds}s`);
            } else {
                setTimer("Time expired");
                // Optionally call cancel function here
                // handleCancelBooking(responseToken!, booking!.reference);
            }
        };

        updateTimer();

        const timerInterval = setInterval(updateTimer, 1000);

        return () => clearInterval(timerInterval);
    };

    const handleCancelBooking = async (responseToken: string, reference: string) => {
        try {
            // Step 1: Show confirmation dialog using SweetAlert2
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: 'Do you really want to cancel this booking? This action cannot be undone.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, Cancel it!',
                cancelButtonText: 'No, Keep it',
            });

            // Step 2: If the user confirms, proceed with cancellation
            if (result.isConfirmed) {

                // Now proceed with other operations after the success message
                await cancelBooking(responseToken, reference);
                await fetchDetails(); // Fetch the latest details after cancellation

                await Swal.fire({
                    title: 'Cancelled!',
                    text: 'Your booking has been cancelled.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#4CAF50', // Green color for the OK button
                });

                const createdBookingDetails = await getBookingDetails(booking?.responseToken!);
                await updateBookingInDatabase(
                    createdBookingDetails,
                    user?.userId!,
                    getCurrentDateTime()
                );
                await updateStatus(createdBookingDetails.reference, 'cancelled');
                await sendCancelBookingEmail(bookingFromDB?.email!, booking!, bookingFromDB?.holdOrPaid!);

            }
        } catch (error) {
            console.error("Failed to cancel booking:", error);
            setError("Failed to cancel the booking. Please try again later.");

            // Step 3: Show error message in case of failure
            await Swal.fire({
                title: 'Error!',
                text: 'There was an issue cancelling your booking. Please try again later.',
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#FF0000', // Red color for the OK button
            });
        }
    };

    const fetchRate = async () => {
        try {
            const exchangeRate = await getUsdToGbpExchangeRate();
            setRate(exchangeRate);
        } catch (err: any) {
            console.error('Failed to fetch exchange rate:', err);
        }
    };

    useEffect(() => {
        fetchRate();
    }, []);

    const handleUpdateGuest = () => {
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
    };

    const handleGuestDetailsUpdated = () => {
        fetchDetails();
    };

    if (isLoading) {
        return <p>Loading booking details...</p>;
    }

    if (error) {
        return <p className="text-red-500">{error}</p>;
    }

    if (!booking) {
        return <p>No booking details found.</p>;
    }

    const effectiveRate = rate ?? 0.82;

    const totalAmount = (booking.grandTotal[0].amount * effectiveRate * 1.04).toFixed(2);
    const balanceAmount = (booking.grandTotal[0].amount * effectiveRate * 1.04 - parseFloat(bookingFromDB?.paidAmount || '0')).toFixed(2);



    const handlePayNow = () => {
        navigate(`/booking/${responseToken}`, {
            state: {
                numAdults: adultCount,
                numChilds: childCount,
                totalCost: balanceAmount === '-0.00' ? totalAmount : balanceAmount,
                hotel: hotel,
                hold: bookingFromDB?.holdOrPaid === 'hold' ? 'hold' : 'paid-deposit',
                bookingResponse: booking.responseToken,
                email: bookingFromDB?.email,
                reference: booking.reference
            }
        });
    };

    const totalAdults = booking.paxGroups.reduce((sum, group) => sum + (group.numAdults || 0), 0);
    const totalChildren = booking.paxGroups.reduce((sum, group) => sum + (group.numChildren || 0), 0);

    // Calculate total count
    const totalCount = totalAdults + totalChildren;
    const perPerson = (booking.grandTotal[0].amount * effectiveRate * 1.04) / totalCount;

    return (
        <div className="container mx-auto p-6 mt-10">
            <h1 className="text-2xl md:text-3xl font-bold mb-8 text-center">Booking Details</h1>
            <div className="bg-halo-pink-bg p-6 rounded-lg shadow-lg border-t">
                {/* General Booking Information */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="mb-6">
                        {holdOrPaid === 'hold' && (
                            <div className="md:flex justify-between items-center mb-4">
                                <h2 className="text-xl md:text-2xl font-bold text-gray-800">{booking.subject}</h2>
                                <div className="text-red-600 font-semibold text-lg mt-4 md:mt-0">
                                    {timer || 'Calculating...'}
                                </div>
                            </div>
                        )}
                        {holdOrPaid !== 'hold' && (
                            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">{booking.subject}</h2>
                        )}
                        <hr className="mb-4 mt-2" />
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <p className="text-gray-700">
                                <span className="font-semibold">Reference:</span> {booking.reference}
                            </p>
                            <p className="text-gray-700">
                                <span className="font-semibold">Dates:</span> {booking.tripStartDate} - {booking.tripEndDate}
                            </p>
                            <p className="text-gray-700">
                                <span className="font-semibold">Booking Date:</span> {booking.bookedDate}
                            </p>
                            <p className="text-gray-700">
                                <span className="font-semibold">Guest:</span> {booking.primaryGuestName}
                            </p>
                            <p className="text-gray-700">
                                <span className="font-semibold">Payment Status:</span>
                                <span
                                    className={`ml-2 px-2 py-1 rounded-md text-white ${holdOrPaid === 'paid' || holdOrPaid === 'paid-error'
                                        ? 'bg-green-500'
                                        : booking.status === 'pending'
                                            ? 'bg-yellow-500'
                                            : booking.status === 'confirmed'
                                                ? 'bg-green-500'
                                                : booking.status === 'cancelled'
                                                    ? 'bg-red-500'
                                                    : 'bg-gray-500'
                                        }`}
                                >
                                    {holdOrPaid === 'paid' || holdOrPaid === 'paid-error'
                                        ? 'Confirmed'
                                        : booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                                </span>
                            </p>
                            <p className="text-gray-700">
                                <span className="font-semibold">Paid Amount:</span> £ {bookingFromDB?.paidAmount === "" ? '0.00' : bookingFromDB?.paidAmount}
                            </p>
                            <p className="text-gray-700 mt-2">
                                <span className="font-semibold">Total Amount:</span> GBP{" "} {totalAmount}
                            </p>
                            <p className="text-gray-700 mt-2">
                                <span className="font-semibold">Balance Amount:</span> £ {balanceAmount}
                            </p>
                        </div>
                        <div className="mt-4">
                            <p className="text-gray-700 mt-2">
                                <span className="font-semibold">Remarks:</span> {booking.remarks || 'None'}
                            </p>
                            <p className="text-gray-700 mt-2">
                                <span className="font-semibold">Booking Status:</span>
                                <span
                                    className={`ml-2 px-2 py-1 rounded-md text-white ${booking.bookingStatus === 'pending'
                                        ? 'bg-yellow-500'
                                        : booking.bookingStatus === 'confirmed'
                                            ? 'bg-green-500'
                                            : booking.bookingStatus === 'cancelled'
                                                ? 'bg-red-500'
                                                : 'bg-gray-500'
                                        }`}
                                >
                                    {booking.bookingStatus.charAt(0).toUpperCase() + booking.bookingStatus.slice(1)}
                                </span>
                            </p>
                        </div>
                        <div className="flex justify-end items-center mt-6">
                            {/* <h3 className="text-2xl font-bold text-gray-800">Hotel Details</h3> */}
                            {(holdOrPaid === 'hold' || balanceAmount !== '-0.00') && (
                                <button
                                    className="bg-halo-pink text-white px-4 py-2 font-semibold hover:bg-pink-800 rounded shadow-lg transition duration-200"
                                    onClick={handlePayNow}
                                >
                                    Pay Now
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Hotels Section */}
                <div className="mb-6 mt-6">
                    {/* <div className="flex justify-between items-center mb-6 mt-4">
                        <h3 className="text-2xl font-bold text-gray-800">Hotel Details</h3>
                        {holdOrPaid === 'hold' && (
                            <button
                                className="bg-pink-500 text-white px-4 py-2 font-semibold hover:bg-pink-600 rounded-lg shadow-lg transition duration-200"
                                onClick={handlePayNow}
                            >
                                Pay Now
                            </button>
                        )}
                    </div> */}
                    {booking.hotels.map((hotel, index) => (
                        <div
                            key={index}
                            className="border rounded-lg bg-white p-6 mb-6 shadow-md hover:shadow-lg transition duration-200"
                        >
                            {/* Hotel Name */}
                            <h2 className="text-lg md:text-xl font-bold text-gray-800">Holiday Details</h2>
                            <hr className="mb-4 mt-2" />

                            {/* Room Details */}
                            {hotel.groups.map((group, idx) => (
                                <div key={idx} className="mt-4">
                                    <p className="text-gray-700">{group.description.join(", ")}</p>
                                    {group.rooms.map((room, roomIndex) => (
                                        <div key={roomIndex} className="mt-4 pl-4 border-l-4 border-halo-pink">
                                            <p className="text-gray-700 mt-1">
                                                <span className="font-medium">Room Type:</span>{" "}
                                                {room.name}
                                            </p>
                                            <p className="text-gray-700 mt-1">
                                                <span className="font-medium">Meal Plan:</span>{" "}
                                                {room.info.accommodation.mealPlan.name}
                                            </p>
                                            <p className="text-gray-700">
                                                <span className="font-medium">Check-in:</span> {hotel.checkInDate}
                                            </p>
                                            <p className="text-gray-700">
                                                <span className="font-medium">Check-out:</span> {hotel.checkOutDate}
                                            </p>
                                            <hr className="mb-4 mt-2" />
                                            <p className="text-lg text-gray-700 mt-1">
                                                <span className="font-medium">Price:</span>{" "}
                                                <span className="text-[#0050b8]">GBP{" "}
                                                    {perPerson.toFixed(2)}</span>{" "}<span className="text-sm">Per Person</span>
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ))}

                            {/* Cancellation Policies */}
                            {/* <div className="mt-6">
                                <h4 className="font-bold text-gray-900 mb-2">Cancellation Policies:</h4>
                                {hotel.cancellationPolicies.map((policy, pIndex) => (
                                    <div key={pIndex} className="pl-4 border-l-4 border-pink-500 mb-4">
                                        <p className="text-gray-700">
                                            <span className="font-medium">Description:</span>{" "}
                                            {policy.description.join(", ")}
                                        </p>
                                        <p className="text-gray-700">
                                            <span className="font-medium">Amount:</span> {policy.amount} (
                                            {policy.amountType})
                                        </p>
                                        <p className="text-gray-700">
                                            <span className="font-medium">Applies On:</span> {policy.applyOn}
                                        </p>
                                    </div>
                                ))}
                            </div> */}
                        </div>
                    ))}
                </div>


                {/* Pax Groups Section */}
                <div className="mb-8">
                    {booking.paxGroups.map((group, index) => (
                        <div
                            key={index}
                            className="border rounded-lg bg-white p-6 mb-6 shadow-md hover:shadow-lg transition duration-200"
                        >
                            <h2 className="text-lg md:text-xl font-bold text-gray-800">Pax Details</h2>
                            <hr className="mb-4 mt-2" />

                            <div className="md:flex items-center justify-between mt-4 pl-4 border-l-4 border-halo-pink">
                                {/* <h4 className="text-lg font-bold text-gray-800 mb-4">Guest Details</h4> */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {group.guestDetails.map((guest, idx) => (
                                        <div
                                            key={idx}
                                            className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                                        >
                                            <p className="text-gray-700 font-medium">
                                                {idx === 0 ? "Primary Pax" : `Pax ${idx + 1}`}:{" "}
                                                <span className="font-normal">
                                                    {guest.firstName || "N/A"} {guest.lastName || "N/A"}
                                                </span>
                                            </p>
                                            <p className="text-gray-700 font-medium">Type: <span className="font-normal">{guest.type}</span></p>
                                            {/* <div className="mt-3">
                                                <div className="text-gray-700 font-semibold mb-2">Flight Details:</div>
                                                {guest.flightDetails.map((flight, fIdx) => (
                                                    <p key={fIdx} className="text-sm text-gray-600">
                                                        <span className="font-medium text-gray-700">{flight.type}:</span> {flight.travelDate} - {flight.destinationCode}
                                                        {flight.flightNo ? ` (Flight No: ${flight.flightNo})` : ""}
                                                    </p>
                                                ))}
                                            </div> */}
                                        </div>
                                    ))}
                                </div>
                                <button
                                    onClick={handleUpdateGuest}
                                    className={`bg-green-500 text-white px-5 py-2 rounded mt-4 md:mt-0 text-sm font-medium shadow hover:bg-green-600 h-10 transition ${booking.bookingStatus === 'cancelled' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    disabled={booking.bookingStatus === 'cancelled'}
                                >
                                    Update Guest Details
                                </button>
                            </div>

                            <hr className="mb-2 mt-4" />

                            <div className="flex justify-between items-start md:items-center">
                                <div className="mt-4 md:mb-0 grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* <p className="text-lg font-semibold text-gray-700">Group Key: {group.key}</p> */}
                                    {/* <p className="text-gray-600">
                                        Total Pax: {group.totalPax}
                                        <span className="text-gray-500">(Adults: {group.numAdults}, Children: {group.numChildren})</span>
                                    </p> */}
                                    <div className="border-l-4 border-[#0050b8] pl-4">
                                        <p className="text-gray-700">
                                            <span className="font-medium min-w-[80px] inline-block">Travellers:</span> {group.numAdults} Adults, {group.numChildren} Children
                                        </p>
                                        <p className="text-gray-700">
                                            <span className="font-medium min-w-[80px] inline-block">Total Pax:</span> {group.totalPax}
                                        </p>
                                    </div>
                                </div>

                            </div>
                        </div>
                    ))}
                </div>


                {/* Transfers Section */}
                {/* <div className="mb-6">
                    <h3 className="text-xl font-bold mb-4">Transfers</h3>
                    {booking.transfers.map((transfer, index) => (
                        <div key={index} className="border p-4 rounded-lg mb-4 bg-gray-50">
                            <p className="font-medium">Transfer on: {transfer.startDate}</p>
                            <p>{transfer.description.join(", ")}</p>
                            <p>Price: {transfer.price.currencyCode} {(transfer.price.amount * 1.04).toFixed(2)}</p>
                        </div>
                    ))}
                </div> */}

                {/* Destination Services Section */}
                {/* <div className="mb-6">
                    <h3 className="text-xl font-bold mb-4">Destination Services</h3>
                    {booking.destinationServices.map((service, index) => (
                        <div key={index} className="border p-4 rounded-lg mb-4 bg-gray-50">
                            <p className="font-medium">{service.serviceName}</p>
                            <p>{service.description.join(", ")}</p>
                            <p>Price: {service.price.currencyCode} {(service.price.amount * 1.04).toFixed(2)}</p>
                        </div>
                    ))}
                </div> */}

                {/* Grand Total */}
                <div className="border rounded-lg bg-white p-6 mb-6 shadow-md hover:shadow-lg transition duration-200">
                    <div className="mt-6 flex flex-wrap justify-between items-center">
                        {/* Grand Total Section */}
                        <div className="mb-4 sm:mb-0">
                            <h3 className="text-lg font-bold text-gray-800">Grand Total</h3>
                            <p className={`text-2xl font-semibold ${booking.bookingStatus === 'cancelled' ? 'text-gray-500' : 'text-green-700'}`}>
                                GBP{" "} {(booking.grandTotal[0].amount * effectiveRate * 1.04).toFixed(2)}
                            </p>
                        </div>

                        {/* Cancel Button Section */}
                        <div>
                            <button
                                onClick={() => {
                                    if (booking.bookingStatus !== 'cancelled') {
                                        // Trigger SweetAlert2 confirmation before proceeding
                                        handleCancelBooking(booking.responseToken, booking.reference);
                                    }
                                }}
                                className={`px-4 py-2 rounded font-medium transition-all duration-200
            ${booking.bookingStatus === 'cancelled'
                                        ? 'bg-gray-400 text-white cursor-not-allowed'
                                        : 'bg-red-600 text-white hover:bg-red-800'
                                    }`}
                                disabled={booking.bookingStatus === 'cancelled'}
                                aria-disabled={booking.bookingStatus === 'cancelled'}
                            >
                                {booking.bookingStatus === 'cancelled' ? 'Booking Cancelled' : 'Cancel Booking'}
                            </button>
                        </div>
                    </div>
                </div>


                {/* {holdOrPaid === 'hold' &&
                    <StripePayment price={(booking.grandTotal[0].amount * 1.04).toFixed(2)} reference={booking.reference} 
                    bookingStatus={booking.bookingStatus} responseToken={booking.responseToken} booking={booking} passengerEmail={bookingFromDB?.email!}/>
                } */}
            </div>

            {/* Modal for Update Guest Details */}
            {showModal && (
                <UpdateGuestDetailsForm
                    closeModal={closeModal}
                    bookingResponseToken={booking.responseToken}
                    onGuestDetailsUpdated={handleGuestDetailsUpdated}
                />
            )}
        </div>
    );
};

export default BookingDetails;
